/**
 * Utility functions for displaying opportunities in tables
 * Uses ASCII Unit Separator (x1F) for delimiting opportunities
 */

const OPPORTUNITY_DELIMITER = "\x1F";

/**
 * Parses opportunity names from delimited string
 */
export function parseOpportunityNames(value: unknown): string[] {
  if (!value || typeof value !== "string") return [];

  // Split by ASCII delimiter
  return value.split(OPPORTUNITY_DELIMITER).filter(Boolean);
}

/**
 * Parses opportunity IDs from delimited string
 */
export function parseOpportunityIds(value: unknown): string[] {
  if (!value || typeof value !== "string") return [];

  // Split by ASCII delimiter
  return value.split(OPPORTUNITY_DELIMITER).filter(Boolean);
}

/**
 * Formats opportunities for CSV export
 * Returns a clean comma-separated string with proper escaping
 * Supports both new JSON format and legacy delimited strings
 */
export function formatOpportunitiesForCSV(value: unknown): string {
  // Check if it's the new JSON format
  if (Array.isArray(value)) {
    const opportunities = value.map((opp: { name?: string } | string) =>
      typeof opp === "object" && opp !== null ? opp.name || "" : String(opp),
    );
    return opportunities
      .map((opp) => `"${opp.replace(/"/g, '""')}"`)
      .join(", ");
  }

  // Fall back to legacy delimited string format
  const opportunities = parseOpportunityNames(value);
  return opportunities.map((opp) => `"${opp.replace(/"/g, '""')}"`).join(", ");
}

/**
 * Gets opportunity data from row
 * Supports both new JSON format (opportunities array) and legacy format (delimited strings)
 */
export function getOpportunityData(item: unknown): {
  ids: string[];
  names: string[];
} {
  const typedItem = item as {
    opportunities_involved?: unknown;
    opportunity_ids?: unknown;
    opportunities?: Array<{
      id: string;
      name: string;
      account_id: string;
    }>;
  };

  // Check for new JSON format first
  if (typedItem.opportunities && Array.isArray(typedItem.opportunities)) {
    const names = typedItem.opportunities.map((opp) => opp.name);
    const ids = typedItem.opportunities.map((opp) => opp.id);
    return { names, ids };
  }

  // Fall back to legacy delimited string format
  const names = parseOpportunityNames(typedItem.opportunities_involved);
  const ids = parseOpportunityIds(typedItem.opportunity_ids);

  // Validate that arrays have matching lengths
  if (names.length !== ids.length) {
    console.error(
      `Mismatch between opportunity names (${names.length}) and IDs (${ids.length})`,
    );
    // Return empty arrays to prevent display issues
    return { names: [], ids: [] };
  }

  return { names, ids };
}
